import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import ScrollToTop from '@/components/ScrollToTop';

// Auth pages
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import AuthCallback from '@/pages/AuthCallback';
import ActivatePage from '@/pages/ActivatePage';
import ApplyPage from '@/pages/ApplyPage';

// Public website pages
import PublicLayout from '@/components/PublicLayout';
import HomePage from '@/pages/public/HomePage';
import LocationsPage from '@/pages/public/LocationsPage';
import HowItWorksPage from '@/pages/public/HowItWorksPage';
import ForPractitionersPage from '@/pages/public/ForPractitionersPage';
import BookViewingPage from '@/pages/public/BookViewingPage';
import WhyWeExistPage from '@/pages/public/WhyWeExistPage';
import PrivacyPage from '@/pages/public/PrivacyPage';
import UnsubscribePage from '@/pages/public/UnsubscribePage';
import ClaphamGuidePage from '@/pages/public/ClaphamGuidePage';

// Operator pages
import OperatorLayout from '@/components/OperatorLayout';
import OperatorDashboard from '@/pages/operator/Dashboard';
import SitesPage from '@/pages/operator/Sites';
import StudiosPage from '@/pages/operator/Studios';
import EnquiriesPage from '@/pages/operator/Enquiries';
import OccupiersPage from '@/pages/operator/Occupiers';
import PaymentsPage from '@/pages/operator/Payments';
import TicketsPage from '@/pages/operator/Tickets';
import SiteOpsPage from '@/pages/operator/SiteOps';
import SecurityPage from '@/pages/operator/Security';
import SubscribersPage from '@/pages/operator/Subscribers';

// Practitioner pages
import PractitionerLayout from '@/components/PractitionerLayout';
import PractitionerDashboard from '@/pages/practitioner/Dashboard';
import MyPayments from '@/pages/practitioner/MyPayments';
import MyContract from '@/pages/practitioner/MyContract';
import MyAccess from '@/pages/practitioner/MyAccess';
import HouseRules from '@/pages/practitioner/HouseRules';
import WelcomePage from '@/pages/practitioner/Welcome';
import ReportIssue from '@/pages/practitioner/ReportIssue';
import ProfilePage from '@/pages/practitioner/Profile';

const OPERATOR_ROLES = ['operator_admin', 'site_manager', 'admin'];
const PRACTITIONER_ROLES = ['practitioner'];

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-6 h-6 text-[#3A4035] animate-spin mx-auto mb-3" />
        <p className="text-sm text-[#5C5C55] font-body">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user === false || user === null) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'practitioner' ? '/my-studio' : '/dashboard'} replace />;
  }
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user && user !== false) {
    return <Navigate to={user.role === 'practitioner' ? '/my-studio' : '/dashboard'} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id during render (synchronous), NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* ── PUBLIC WEBSITE ── */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/for-practitioners" element={<ForPractitionersPage />} />
        <Route path="/book-a-viewing" element={<BookViewingPage />} />
        <Route path="/our-purpose" element={<WhyWeExistPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        {/* Redirect old URL */}
        <Route path="/why-we-exist" element={<WhyWeExistPage />} />
      </Route>

      {/* ── STUDIO GUIDE (standalone, no public nav) ── */}
      <Route path="/studio-guide/clapham" element={<ClaphamGuidePage />} />

      {/* ── AUTH PAGES ── */}
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/apply" element={<ApplyPage />} />

      {/* ── OPERATOR PLATFORM ── */}
      <Route element={<ProtectedRoute allowedRoles={OPERATOR_ROLES}><OperatorLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<OperatorDashboard />} />
        <Route path="/sites" element={<SitesPage />} />
        <Route path="/studios" element={<StudiosPage />} />
        <Route path="/enquiries" element={<EnquiriesPage />} />
        <Route path="/occupiers" element={<OccupiersPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/site-ops" element={<SiteOpsPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/subscribers" element={<SubscribersPage />} />
      </Route>

      {/* ── PRACTITIONER PLATFORM ── */}
      <Route element={<ProtectedRoute allowedRoles={PRACTITIONER_ROLES}><PractitionerLayout /></ProtectedRoute>}>
        <Route path="/my-studio" element={<PractitionerDashboard />} />
        <Route path="/my-payments" element={<MyPayments />} />
        <Route path="/my-contract" element={<MyContract />} />
        <Route path="/my-access" element={<MyAccess />} />
        <Route path="/house-rules" element={<HouseRules />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/report-issue" element={<ReportIssue />} />
        <Route path="/my-profile" element={<ProfilePage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
